import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import type { Checkpoint, CheckpointFieldType } from '@/graph/domain/types';
import { uploadCheckpointReferenceFile, getCheckpointReferenceFileSignedUrl } from '@/services/editorApi.service';
import { getWorkspaceId } from '@/services/storage.helper';
import { useGraphEditorStore } from '@/graph-store';
import type { EntityExplorerConfig, EntityExplorerResult } from '@/types/entity-explorer.types';
import { getBridge } from '@/services/communicationBridge.service';
import type { EntityType } from '@/types/communication.types';
import { environment } from '@/config/environment.config';

const ILA_BASE = environment.get().ilaBaseUrl;
// ---------------------------------------------------------------------------
// Unit data — mirrors Angular's unitTypeMap
// ---------------------------------------------------------------------------

const UNIT_TYPE_MAP: Record<string, string[]> = {
  acceleration:  ['RPM', 'G'],
  ampUnits:      ['A', 'mA'],
  currency:      ['INR', 'USD', 'GBP', 'EUR', 'CAD', 'AUD'],
  florateVolUS:  ['L/hour', 'L/min', 'L/sec', 'mL/hour', 'mL/min', 'mL/sec', 'µL/hour', 'µL/min', 'µL/sec'],
  frequency:     ['Hz'],
  shakingSeed:   ['RPM'],
  siUnits: [
    'L/hour', 'L/min', 'L/sec', 'mL/hour', 'mL/min', 'mL/sec', 'µL/hour', 'µL/min', 'µL/sec',
    'RPM', 'G', 'A', 'mA', 'kg', 'gm', 'mg', 'µg', 'Hz', '°C', '°F', '°K',
    'V', 'mV', 'kV', 'L', 'mL', 'µL', 'nm', 'N/m2', 'N-m', 'm/sec2', 'cm/s2',
    'm/s', 'm', 'm2', 'm3', 'm/sec', 'Newton(kg.m/s2)', 'N/m', 'Joule(N-m)',
    'volt/meter', 'radian/sec', 'kg.m/s', 'kelvin(k)', 'kg/m3', 'joule/sec (watt)',
    'Tesla', 'Theta', 'N/kg', 'Mole', 'ml/gm',
  ],
  speed:         ['nm/min'],
  time:          ['Hours', 'Minutes', 'Seconds'],
  usage:         ['Hours', 'Usages'],
  liquidVolUS:   ['L', 'mL', 'µL'],
  wavelength:    ['nm'],
  weight:        ['kg', 'gm', 'mg', 'µg'],
  voltage:       ['V', 'mV', 'kV'],
  temperature:   ['°C', '°F', '°K'],
  massRange:     ['m/z'],
};

function formatUnitLabel(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/\b\w/g, (c) => c.toUpperCase());
}

const UNIT_CATEGORY_OPTIONS = Object.keys(UNIT_TYPE_MAP).map((k) => ({
  value: k,
  label: formatUnitLabel(k),
}));

const UNIT_SOURCE_OPTIONS = [
  { value: 'none',     label: 'No Units' },
  { value: 'existing', label: 'Existing' },
  { value: 'custom',   label: 'Add New' },
];

// ---------------------------------------------------------------------------
// Field type options — mirrors Angular defaultCheckpointFieldTypeOptions
// ---------------------------------------------------------------------------

const FIELD_TYPES: { value: CheckpointFieldType; label: string }[] = [
  { value: 'text',       label: 'Only Text' },
  { value: 'textNumber', label: 'Text & Numbers' },
  { value: 'number',     label: 'Only Numbers' },
  { value: 'date',       label: 'Date' },
  { value: 'file',       label: 'File Upload' },
  { value: 'user',       label: 'User' },
  { value: 'dropdown',   label: 'Dropdown' },
  { value: 'units',      label: 'TextNumber & Units' },
  { value: 'range',      label: 'Range' },
  { value: 'group',      label: 'Group' },
  { value: 'linkEntity', label: 'Link Entity' },
];

// Child types exclude 'group' — mirrors defaultCheckpointFieldTypeOptionsChild
const CHILD_FIELD_TYPES = FIELD_TYPES.filter((ft) => ft.value !== 'group');

// ---------------------------------------------------------------------------
// Internal state types
// ---------------------------------------------------------------------------

type UnitSource = 'none' | 'existing' | 'custom';
interface DropdownEntry { id: string; label: string }
interface UnitEntry    { id: string; value: string }

interface FileReference {
  fileName?: string;
  fileUrl?: string;
  bucket?: string;
  raw?: { filePath?: string; bucket?: string; workspaceId?: string; [k: string]: unknown };
}

interface SpreadsheetReference {
  entityId?: string;      // Spreadsheet file ID
  versionId?: string;     // Spreadsheet version ID
  name?: string;
}

interface ChildFieldState {
  id: string;
  fieldName: string;
  fieldType: CheckpointFieldType;
  dropdown: DropdownEntry[];
  unitSource: UnitSource;
  unitCategory: string;
  unitValuesArray: UnitEntry[];
  rangeMin: string;
  rangeMax: string;
  referenceFile?: FileReference;
  referenceSpreadsheetFile?: SpreadsheetReference;
}

interface FormState {
  checkpointName: string;
  fieldType: CheckpointFieldType;
  dropdown: DropdownEntry[];
  unitSource: UnitSource;
  unitCategory: string;
  unitValuesArray: UnitEntry[];
  rangeMin: string;
  rangeMax: string;
  children: ChildFieldState[];
  referenceFile?: FileReference;
  referenceSpreadsheetFile?: SpreadsheetReference;
}

interface ChildErrors {
  fieldName?: string;
  dropdown?: string;
  rangeMin?: string;
  rangeMax?: string;
  unitCategory?: string;
  unitValuesArray?: string;
}

interface FormErrors {
  checkpointName?: string;
  dropdown?: string;
  rangeMin?: string;
  rangeMax?: string;
  unitCategory?: string;
  unitValuesArray?: string;
  children?: Record<number, ChildErrors>;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AddCheckpointDialogProps {
  open: boolean;
  checkpointToEdit?: Checkpoint | null;
  existingNames: string[];
  operationTypeId?: string;
  /** Workspace ID - used to construct default storage container for old files */
  workspaceId?: string;
  onConfirm: (checkpoint: Checkpoint) => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function genId(): string {
  return 'cp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
}

const DEFAULT_STATE: FormState = {
  checkpointName:  '',
  fieldType:       'text',
  dropdown:        [],
  unitSource:      'existing',
  unitCategory:    '',
  unitValuesArray: [],
  rangeMin:        '',
  rangeMax:        '',
  children:        [],
  referenceFile:   undefined,
  referenceSpreadsheetFile: undefined,
};

function checkpointToChild(cp: Checkpoint): ChildFieldState {
  return {
    id:              cp.id ?? genId(),
    fieldName:       cp.checkpointName,
    fieldType:       cp.fieldType,
    dropdown:        (cp.dropdown ?? []).map((d) => ({ id: genId(), label: d.label })),
    unitSource:      (cp.unitSource as UnitSource) ?? 'existing',
    unitCategory:    cp.unitCategory ?? '',
    unitValuesArray: (cp.units ?? []).map((u) => ({ id: genId(), value: u })),
    rangeMin:        cp.rangeMin != null ? String(cp.rangeMin) : '',
    rangeMax:        cp.rangeMax != null ? String(cp.rangeMax) : '',
    referenceFile:   (cp as any).referenceFile ? { 
      fileName: (cp as any).referenceFile.fileName, 
      fileUrl: (cp as any).referenceFile.fileUrl, 
      raw: (cp as any).referenceFile 
    } : undefined,
    referenceSpreadsheetFile: (cp as any).referenceSpreadsheetFile ? { 
      entityId: (cp as any).referenceSpreadsheetFile.entityId, 
      versionId: (cp as any).referenceSpreadsheetFile.versionId,
      name: (cp as any).referenceSpreadsheetFile.name 
    } : undefined,
  };
}

function toFormState(cp: Checkpoint): FormState {
  return {
    checkpointName:  cp.checkpointName,
    fieldType:       cp.fieldType,
    dropdown:        (cp.dropdown ?? []).map((d) => ({ id: genId(), label: d.label })),
    unitSource:      (cp.unitSource as UnitSource) ?? 'existing',
    unitCategory:    cp.unitCategory ?? '',
    unitValuesArray: (cp.units ?? []).map((u) => ({ id: genId(), value: u })),
    rangeMin:        cp.rangeMin != null ? String(cp.rangeMin) : '',
    rangeMax:        cp.rangeMax != null ? String(cp.rangeMax) : '',
    children:        (cp.children ?? []).map(checkpointToChild),
    referenceFile:   (cp as any).referenceFile ? { 
      fileName: (cp as any).referenceFile.fileName, 
      fileUrl: (cp as any).referenceFile.fileUrl, 
      raw: (cp as any).referenceFile 
    } : undefined,
    referenceSpreadsheetFile: (cp as any).referenceSpreadsheetFile ? { 
      entityId: (cp as any).referenceSpreadsheetFile.entityId, 
      versionId: (cp as any).referenceSpreadsheetFile.versionId,
      name: (cp as any).referenceSpreadsheetFile.name 
    } : undefined,
  };
}

function defaultChild(): ChildFieldState {
  return {
    id: genId(), fieldName: '', fieldType: 'text',
    dropdown: [], unitSource: 'existing', unitCategory: '',
    unitValuesArray: [], rangeMin: '', rangeMax: '',
    referenceFile: undefined,
    referenceSpreadsheetFile: undefined,
  };
}

// ---------------------------------------------------------------------------
// Reusable sub-sections
// ---------------------------------------------------------------------------

function DropdownSection({ dropdown, error, submitted, onAdd, onRemove, onChange }: {
  dropdown: DropdownEntry[];
  error?: string;
  submitted: boolean;
  onAdd: (label: string) => void;
  onRemove: (id: string) => void;
  onChange: (id: string, label: string) => void;
}) {
  const [newLabel, setNewLabel] = useState('');
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Dropdown Entries
      </Typography>
      {dropdown.map((entry) => (
        <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <TextField size="small" fullWidth placeholder="Enter option"
            value={entry.label} onChange={(e) => onChange(entry.id, e.target.value)}
          />
          <IconButton size="small" color="error" onClick={() => onRemove(entry.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
        <TextField size="small" placeholder="Enter option" sx={{ flex: 1 }}
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newLabel.trim()) { onAdd(newLabel.trim()); setNewLabel(''); } } }}
        />
        <Button size="small" variant="outlined" startIcon={<AddIcon />}
          onClick={() => { if (newLabel.trim()) { onAdd(newLabel.trim()); setNewLabel(''); } }}>
          Add Entry
        </Button>
      </Box>
      {submitted && error && <Typography variant="caption" color="error">{error}</Typography>}
    </Box>
  );
}

function RangeSection({ rangeMin, rangeMax, errors, submitted, onChange }: {
  rangeMin: string; rangeMax: string;
  errors?: { rangeMin?: string; rangeMax?: string };
  submitted: boolean;
  onChange: (min: string, max: string) => void;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Range Configuration
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField size="small" fullWidth label="Min Value" type="number" placeholder="Enter minimum value"
          value={rangeMin} onChange={(e) => onChange(e.target.value, rangeMax)}
          error={submitted && !!errors?.rangeMin} helperText={submitted && errors?.rangeMin}
        />
        <TextField size="small" fullWidth label="Max Value" type="number" placeholder="Enter maximum value"
          value={rangeMax} onChange={(e) => onChange(rangeMin, e.target.value)}
          error={submitted && !!errors?.rangeMax} helperText={submitted && errors?.rangeMax}
        />
      </Box>
    </Box>
  );
}

function UnitsSection({ unitSource, unitCategory, unitValuesArray, errors, submitted,
  onSourceChange, onCategoryChange, onUnitAdd, onUnitRemove, onUnitChange }: {
  unitSource: UnitSource; unitCategory: string; unitValuesArray: UnitEntry[];
  errors?: { unitCategory?: string; unitValuesArray?: string };
  submitted: boolean;
  onSourceChange: (v: UnitSource) => void;
  onCategoryChange: (v: string) => void;
  onUnitAdd: (value: string) => void;
  onUnitRemove: (id: string) => void;
  onUnitChange: (id: string, value: string) => void;
}) {
  const [newUnit, setNewUnit] = useState('');
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        <TextField select size="small" label="Units" sx={{ flex: 1 }}
          value={unitSource} onChange={(e) => onSourceChange(e.target.value as UnitSource)}>
          {UNIT_SOURCE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>

        {unitSource === 'existing' && (
          <TextField select size="small" label="Select Units" sx={{ flex: 1 }}
            value={unitCategory} onChange={(e) => onCategoryChange(e.target.value)}
            error={submitted && !!errors?.unitCategory} helperText={submitted && errors?.unitCategory}>
            {UNIT_CATEGORY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        )}
      </Box>

      {unitSource === 'custom' && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Unit Values
          </Typography>
          {unitValuesArray.map((entry) => (
            <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TextField size="small" fullWidth placeholder="Enter unit value"
                value={entry.value} onChange={(e) => onUnitChange(entry.id, e.target.value)}
              />
              <IconButton size="small" color="error" onClick={() => onUnitRemove(entry.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" placeholder="Enter unit value" sx={{ flex: 1 }}
              value={newUnit} onChange={(e) => setNewUnit(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newUnit.trim()) { onUnitAdd(newUnit.trim()); setNewUnit(''); } } }}
            />
            <Button size="small" variant="outlined" startIcon={<AddIcon />}
              onClick={() => { if (newUnit.trim()) { onUnitAdd(newUnit.trim()); setNewUnit(''); } }}>
              Add Unit
            </Button>
          </Box>
          {submitted && errors?.unitValuesArray && (
            <Typography variant="caption" color="error">{errors.unitValuesArray}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function FileSection({ referenceFile, referenceSpreadsheetFile, onUploadFile, onRemoveFile, onLinkSpreadsheet, onRemoveSpreadsheet, onOpenFile, isUploading, uploadError }: {
  referenceFile?: FileReference;
  referenceSpreadsheetFile?: SpreadsheetReference;
  onUploadFile: () => void;
  onRemoveFile: () => void;
  onLinkSpreadsheet: () => void;
  onRemoveSpreadsheet: () => void;
  onOpenFile?: () => void;
  isUploading?: boolean;
  uploadError?: string;
}) {
  const openSpreadsheetVersion = () => {
    if (!referenceSpreadsheetFile?.versionId) return;
    const encodedName = encodeURIComponent(encodeURIComponent(referenceSpreadsheetFile.name || 'spreadsheet'));
    const url = `${ILA_BASE}/folders/SPREADSHEETFILE_VERSION/${encodedName}/${referenceSpreadsheetFile.versionId}/edit`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        File References
      </Typography>

      {/* Reference File Upload */}
      <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
          Reference File
        </Typography>
        {referenceFile?.fileName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
              {referenceFile.fileName}
            </Typography>
            <Button size="small" variant="outlined" onClick={onOpenFile}>
              Open
            </Button>
            <Button size="small" variant="outlined" color="error" onClick={onRemoveFile}>
              Remove
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={onUploadFile} disabled={isUploading}
              startIcon={isUploading ? <CircularProgress size={14} /> : undefined}>
              {isUploading ? 'Uploading…' : 'Upload File'}
            </Button>
            {uploadError && (
              <Typography variant="caption" color="error">{uploadError}</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Reference Spreadsheet Link */}
      <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
          Reference Spreadsheet
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={onLinkSpreadsheet}>
            Link Spreadsheet
          </Button>
          {referenceSpreadsheetFile?.name && (
            <Chip
              label={referenceSpreadsheetFile.name}
              onDelete={onRemoveSpreadsheet}
              onClick={openSpreadsheetVersion}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function AddCheckpointDialog({
  open, checkpointToEdit, existingNames, operationTypeId, workspaceId, onConfirm, onClose,
}: AddCheckpointDialogProps) {
  const isEditing = checkpointToEdit != null;
  const [form, setForm] = useState<FormState>(DEFAULT_STATE);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);
  const [isUploadingChild, setIsUploadingChild] = useState<Record<number, boolean>>({});
  const [uploadErrorChild, setUploadErrorChild] = useState<Record<number, string>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const childFileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const openModal = useGraphEditorStore((s) => s.openModal);
   const communication = {
     sendNavigate: (entityType: EntityType, entityId: string, entityName?: string) =>
       getBridge().sendEvent('NAVIGATE', { entityType, entityId, entityName }),
   };
  useEffect(() => {
    if (open) {
      setForm(checkpointToEdit ? toFormState(checkpointToEdit) : { ...DEFAULT_STATE });
      setErrors({});
      setSubmitted(false);
      setUploadError(undefined);
      setUploadErrorChild({});
    }
  }, [open, checkpointToEdit]);

  const patch = (p: Partial<FormState>) => setForm((prev) => ({ ...prev, ...p }));

  const handleFieldTypeChange = (ft: CheckpointFieldType) => {
    patch({
      fieldType:       ft,
      dropdown:        ft === 'dropdown' ? form.dropdown : [],
      unitSource:      ft === 'units' ? form.unitSource : 'existing',
      unitCategory:    ft === 'units' ? form.unitCategory : '',
      unitValuesArray: ft === 'units' ? form.unitValuesArray : [],
      rangeMin:        ft === 'range' ? form.rangeMin : '',
      rangeMax:        ft === 'range' ? form.rangeMax : '',
      children:        ft === 'group' ? form.children : [],
    });
  };

  // ---- child helpers -------------------------------------------------------

  const patchChild = (idx: number, p: Partial<ChildFieldState>) =>
    setForm((prev) => ({ ...prev, children: prev.children.map((c, i) => i === idx ? { ...c, ...p } : c) }));

  const addChild = () =>
    setForm((prev) => ({ ...prev, children: [...prev.children, defaultChild()] }));

  const removeChild = (idx: number) =>
    setForm((prev) => ({ ...prev, children: prev.children.filter((_, i) => i !== idx) }));

  const handleChildFieldTypeChange = (idx: number, ft: CheckpointFieldType) => {
    patchChild(idx, {
      fieldType:       ft,
      dropdown:        ft === 'dropdown' ? form.children[idx].dropdown : [],
      unitSource:      ft === 'units' ? form.children[idx].unitSource : 'existing',
      unitCategory:    ft === 'units' ? form.children[idx].unitCategory : '',
      unitValuesArray: ft === 'units' ? form.children[idx].unitValuesArray : [],
      rangeMin:        ft === 'range' ? form.children[idx].rangeMin : '',
      rangeMax:        ft === 'range' ? form.children[idx].rangeMax : '',
    });
  };

  // ---- dropdown helpers ----------------------------------------------------

  const addDropdown    = (lbl: string) => patch({ dropdown: [...form.dropdown, { id: genId(), label: lbl }] });
  const removeDropdown = (id: string)  => patch({ dropdown: form.dropdown.filter((o) => o.id !== id) });
  const changeDropdown = (id: string, lbl: string) =>
    patch({ dropdown: form.dropdown.map((o) => o.id === id ? { ...o, label: lbl } : o) });

  const addChildDropdown    = (idx: number, lbl: string) =>
    patchChild(idx, { dropdown: [...form.children[idx].dropdown, { id: genId(), label: lbl }] });
  const removeChildDropdown = (idx: number, id: string) =>
    patchChild(idx, { dropdown: form.children[idx].dropdown.filter((o) => o.id !== id) });
  const changeChildDropdown = (idx: number, id: string, lbl: string) =>
    patchChild(idx, { dropdown: form.children[idx].dropdown.map((o) => o.id === id ? { ...o, label: lbl } : o) });

  // ---- unit helpers --------------------------------------------------------

  const addUnit    = (v: string)  => patch({ unitValuesArray: [...form.unitValuesArray, { id: genId(), value: v }] });
  const removeUnit = (id: string) => patch({ unitValuesArray: form.unitValuesArray.filter((u) => u.id !== id) });
  const changeUnit = (id: string, v: string) =>
    patch({ unitValuesArray: form.unitValuesArray.map((u) => u.id === id ? { ...u, value: v } : u) });

  const addChildUnit    = (idx: number, v: string) =>
    patchChild(idx, { unitValuesArray: [...form.children[idx].unitValuesArray, { id: genId(), value: v }] });
  const removeChildUnit = (idx: number, id: string) =>
    patchChild(idx, { unitValuesArray: form.children[idx].unitValuesArray.filter((u) => u.id !== id) });
  const changeChildUnit = (idx: number, id: string, v: string) =>
    patchChild(idx, { unitValuesArray: form.children[idx].unitValuesArray.map((u) => u.id === id ? { ...u, value: v } : u) });

  // ---- file reference helpers ----------------------------------------------

  const handleUploadFile = () => {
    setUploadError(undefined);
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!operationTypeId) {
      setUploadError('Cannot upload: operation type ID is not available.');
      return;
    }
    setIsUploading(true);
    setUploadError(undefined);
    try {
      const result = await uploadCheckpointReferenceFile(file, operationTypeId);
      patch({ referenceFile: { fileName: result.fileName, fileUrl: result.fileUrl, bucket: result.raw?.bucket, raw: result.raw } });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'File upload failed. Please try again.';
      setUploadError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    patch({ referenceFile: undefined });
    setUploadError(undefined);
  };

  const handleOpenFile = async () => {
    const raw = form.referenceFile?.raw;
    const blobName = form.referenceFile?.fileUrl || raw?.filePath;

    if (!blobName) {
      setUploadError('File path not available');
      return;
    }

    try {
      // Pass blobName only; getCheckpointReferenceFileSignedUrl fetches container dynamically
      const { signedUrl } = await getCheckpointReferenceFileSignedUrl(blobName);
      window.open(signedUrl, '_blank');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to open file';
      setUploadError(message);
    }
  };

  const handleLinkSpreadsheet = () => {
    console.log('[AddCheckpointDialog] handleLinkSpreadsheet called for reference file');
    const config: EntityExplorerConfig = {
      mode: 'import',
      entityTypes: ['SPREADSHEETFILE'],
      title: 'Link Spreadsheet Version',
      forLinkEntity: true,
      multiple: false,
    };

    openModal({
      kind: 'entity-explorer',
      payload: {
        config,
        onConfirm: (result: EntityExplorerResult) => {
          console.log('[AddCheckpointDialog] Entity-explorer result for reference file:', result);
          if (!result || result.kind !== 'import' || !result.items || result.items.length === 0) {
            console.warn('[AddCheckpointDialog] Invalid result from entity-explorer');
            return;
          }
          // Get first item (single select for reference spreadsheet)
          const item = result.items[0];
          console.log('[AddCheckpointDialog] Processing imported item for reference file:', item);
          const spreadsheet = {
            entityId: item.entityId || item._id,
            versionId: item._id,  // Store version ID for backend navigation
            name: item.name || item.preFillName,
          };
          console.log('[AddCheckpointDialog] Setting reference spreadsheet file:', spreadsheet);
          patch({ referenceSpreadsheetFile: spreadsheet });
        },
      },
    });
  };

  const handleRemoveSpreadsheet = () => {
    patch({ referenceSpreadsheetFile: undefined });
  };

  const handleUploadFileChild = (idx: number) => {
    setUploadErrorChild((prev) => ({ ...prev, [idx]: '' }));
    childFileInputRefs.current[idx]?.click();
  };

  const handleChildFileInputChange = async (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!operationTypeId) {
      setUploadErrorChild((prev) => ({ ...prev, [idx]: 'Cannot upload: operation type ID is not available.' }));
      return;
    }
    setIsUploadingChild((prev) => ({ ...prev, [idx]: true }));
    setUploadErrorChild((prev) => ({ ...prev, [idx]: '' }));
    try {
      const result = await uploadCheckpointReferenceFile(file, operationTypeId);
      patchChild(idx, { referenceFile: { fileName: result.fileName, fileUrl: result.fileUrl, bucket: result.raw?.bucket, raw: result.raw } });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'File upload failed. Please try again.';
      setUploadErrorChild((prev) => ({ ...prev, [idx]: message }));
    } finally {
      setIsUploadingChild((prev) => ({ ...prev, [idx]: false }));
    }
  };

  const handleRemoveFileChild = (idx: number) => {
    patchChild(idx, { referenceFile: undefined });
    setUploadErrorChild((prev) => ({ ...prev, [idx]: '' }));
  };

  const handleLinkSpreadsheetChild = (idx: number) => {
    console.log('[AddCheckpointDialog] handleLinkSpreadsheetChild called for reference file, index:', idx);
    const config: EntityExplorerConfig = {
      mode: 'import',
      entityTypes: ['SPREADSHEETFILE'],
      title: 'Link Spreadsheet Version',
      forLinkEntity: true,
      multiple: false,
    };

    openModal({
      kind: 'entity-explorer',
      payload: {
        config,
        onConfirm: (result: EntityExplorerResult) => {
          console.log('[AddCheckpointDialog] Entity-explorer result for child reference file:', result);
          if (!result || result.kind !== 'import' || !result.items || result.items.length === 0) {
            console.warn('[AddCheckpointDialog] Invalid result from entity-explorer for child');
            return;
          }
          // Get first item (single select for reference spreadsheet)
          const item = result.items[0];
          console.log('[AddCheckpointDialog] Processing imported item for child reference file:', item);
          const spreadsheet = {
            entityId: item.entityId || item._id,
            versionId: item._id,  // Store version ID for backend navigation
            name: item.name || item.preFillName,
          };
          console.log('[AddCheckpointDialog] Setting child reference spreadsheet file:', spreadsheet);
          patchChild(idx, { referenceSpreadsheetFile: spreadsheet });
        },
      },
    });
  };

  const handleRemoveSpreadsheetChild = (idx: number) => {
    patchChild(idx, { referenceSpreadsheetFile: undefined });
  };

  const handleOpenFileChild = async (idx: number) => {
    const raw = form.children[idx]?.referenceFile?.raw;
    const blobName = form.children[idx]?.referenceFile?.fileUrl || raw?.filePath;

    if (!blobName) {
      setUploadErrorChild((prev) => ({ ...prev, [idx]: 'File path not available' }));
      return;
    }

    try {
      // Pass blobName only; getCheckpointReferenceFileSignedUrl fetches container dynamically
      const { signedUrl } = await getCheckpointReferenceFileSignedUrl(blobName);
      window.open(signedUrl, '_blank');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to open file';
      setUploadErrorChild((prev) => ({ ...prev, [idx]: message }));
    }
  };

  // ---- validation ----------------------------------------------------------

  const validate = (): boolean => {
    const errs: FormErrors = {};
    let valid = true;

    if (!form.checkpointName.trim()) {
      errs.checkpointName = 'Checkpoint name is required.'; valid = false;
    } else {
      const taken = existingNames.filter((n) => isEditing ? n !== checkpointToEdit!.checkpointName : true);
      if (taken.some((n) => n.toLowerCase() === form.checkpointName.trim().toLowerCase())) {
        errs.checkpointName = 'Checkpoint name already exists.'; valid = false;
      }
    }

    if (form.fieldType === 'dropdown') {
      if (form.dropdown.length === 0) { errs.dropdown = 'At least one dropdown entry is required.'; valid = false; }
      else if (form.dropdown.some((o) => !o.label.trim())) { errs.dropdown = 'All dropdown values are required.'; valid = false; }
    }

    if (form.fieldType === 'range') {
      const min = Number(form.rangeMin), max = Number(form.rangeMax);
      if (form.rangeMin === '') { errs.rangeMin = 'Min value is required.'; valid = false; }
      else if (form.rangeMax === '') { errs.rangeMax = 'Max value is required.'; valid = false; }
      else if (isNaN(min) || isNaN(max) || min >= max) {
        errs.rangeMin = 'Min must be less than Max.'; errs.rangeMax = 'Max must be greater than Min.'; valid = false;
      }
    }

    if (form.fieldType === 'units') {
      if (form.unitSource === 'existing' && !form.unitCategory) {
        errs.unitCategory = 'Please select a unit category.'; valid = false;
      } else if (form.unitSource === 'custom') {
        if (form.unitValuesArray.length === 0) { errs.unitValuesArray = 'At least one unit value is required.'; valid = false; }
        else if (form.unitValuesArray.some((u) => !u.value.trim())) { errs.unitValuesArray = 'All unit values must be filled.'; valid = false; }
      }
    }

    if (form.fieldType === 'group') {
      const childErrs: Record<number, ChildErrors> = {};
      form.children.forEach((child, i) => {
        const ce: ChildErrors = {};
        if (!child.fieldName.trim()) { ce.fieldName = 'Child field name is required.'; valid = false; }
        if (child.fieldType === 'dropdown') {
          if (child.dropdown.length === 0) { ce.dropdown = 'At least one entry required.'; valid = false; }
          else if (child.dropdown.some((o) => !o.label.trim())) { ce.dropdown = 'All entries must be filled.'; valid = false; }
        }
        if (child.fieldType === 'range') {
          const min = Number(child.rangeMin), max = Number(child.rangeMax);
          if (child.rangeMin === '') { ce.rangeMin = 'Min required.'; valid = false; }
          else if (child.rangeMax === '') { ce.rangeMax = 'Max required.'; valid = false; }
          else if (isNaN(min) || isNaN(max) || min >= max) {
            ce.rangeMin = 'Min must be less than Max.'; ce.rangeMax = 'Max must be greater than Min.'; valid = false;
          }
        }
        if (child.fieldType === 'units') {
          if (child.unitSource === 'existing' && !child.unitCategory) { ce.unitCategory = 'Please select a unit category.'; valid = false; }
          else if (child.unitSource === 'custom') {
            if (child.unitValuesArray.length === 0) { ce.unitValuesArray = 'At least one unit required.'; valid = false; }
            else if (child.unitValuesArray.some((u) => !u.value.trim())) { ce.unitValuesArray = 'All unit values must be filled.'; valid = false; }
          }
        }
        if (Object.keys(ce).length) childErrs[i] = ce;
      });
      if (Object.keys(childErrs).length) errs.children = childErrs;
    }

    setErrors(errs);
    return valid;
  };

  // ---- save ----------------------------------------------------------------

  const resolveUnits = (src: UnitSource, cat: string, arr: UnitEntry[]): string[] | undefined => {
    if (src === 'existing' && cat) return UNIT_TYPE_MAP[cat] ?? [];
    if (src === 'custom') return arr.map((u) => u.value.trim()).filter(Boolean);
    return undefined;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const checkpoint: Checkpoint = {
      id:                     checkpointToEdit?.id ?? genId(),
      checkpointName:         form.checkpointName.trim(),
      fieldType:              form.fieldType,
      required:               checkpointToEdit?.required ?? false,
      allowMultiple:          checkpointToEdit?.allowMultiple ?? false,
      allowSpecialCharacters: checkpointToEdit?.allowSpecialCharacters ?? false,
      uniqueAcrossWorkspace:  checkpointToEdit?.uniqueAcrossWorkspace ?? false,
      isCustom:               checkpointToEdit?.isCustom ?? true,
      ...(form.fieldType === 'dropdown' && {
        dropdown: form.dropdown.map((o) => ({ id: o.id, label: o.label })),
      }),
      ...(form.fieldType === 'range' && {
        rangeMin: form.rangeMin !== '' ? Number(form.rangeMin) : undefined,
        rangeMax: form.rangeMax !== '' ? Number(form.rangeMax) : undefined,
      }),
      ...(form.fieldType === 'units' && {
        unitSource:   form.unitSource === 'none' ? undefined : form.unitSource,
        unitCategory: form.unitSource === 'existing' ? form.unitCategory : undefined,
        units:        resolveUnits(form.unitSource, form.unitCategory, form.unitValuesArray),
      }),
      ...(form.fieldType === 'file' && {
        referenceFile:       form.referenceFile,
        referenceSpreadsheetFile: form.referenceSpreadsheetFile,
      }),
      ...(form.fieldType === 'group' && {
        children: form.children.map((child) => ({
          id:                     child.id,
          checkpointName:         child.fieldName.trim(),
          fieldType:              child.fieldType,
          required:               false,
          allowMultiple:          false,
          allowSpecialCharacters: false,
          uniqueAcrossWorkspace:  false,
          isCustom:               true,
          ...(child.fieldType === 'dropdown' && {
            dropdown: child.dropdown.map((o) => ({ id: o.id, label: o.label })),
          }),
          ...(child.fieldType === 'range' && {
            rangeMin: child.rangeMin !== '' ? Number(child.rangeMin) : undefined,
            rangeMax: child.rangeMax !== '' ? Number(child.rangeMax) : undefined,
          }),
          ...(child.fieldType === 'units' && {
            unitSource:   child.unitSource === 'none' ? undefined : child.unitSource,
            unitCategory: child.unitSource === 'existing' ? child.unitCategory : undefined,
            units:        resolveUnits(child.unitSource, child.unitCategory, child.unitValuesArray),
          }),
          ...(child.fieldType === 'file' && {
            referenceFile:       child.referenceFile,
            referenceSpreadsheetFile: child.referenceSpreadsheetFile,
          }),
        })),
      }),
    } as any;

    onConfirm(checkpoint);
  };

  // ---- render --------------------------------------------------------------

  return (
    <>
      {/* Hidden file input for top-level reference file upload */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit Checkpoint' : 'Add Checkpoint'}</DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Row 1: Checkpoint Name + Field Type */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Checkpoint Name" size="small" sx={{ flex: 1 }}
            autoFocus placeholder="Enter checkpoint name"
            value={form.checkpointName}
            onChange={(e) => patch({ checkpointName: e.target.value })}
            error={submitted && !!errors.checkpointName}
            helperText={submitted && errors.checkpointName}
          />
          <TextField
            select label="Field Type" size="small" sx={{ flex: 1 }}
            value={form.fieldType}
            onChange={(e) => handleFieldTypeChange(e.target.value as CheckpointFieldType)}
          >
            {FIELD_TYPES.map((ft) => (
              <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Dropdown Entries */}
        {form.fieldType === 'dropdown' && (
          <DropdownSection
            dropdown={form.dropdown} error={errors.dropdown} submitted={submitted}
            onAdd={addDropdown} onRemove={removeDropdown} onChange={changeDropdown}
          />
        )}

        {/* Range Configuration */}
        {form.fieldType === 'range' && (
          <RangeSection
            rangeMin={form.rangeMin} rangeMax={form.rangeMax}
            errors={{ rangeMin: errors.rangeMin, rangeMax: errors.rangeMax }}
            submitted={submitted}
            onChange={(min, max) => patch({ rangeMin: min, rangeMax: max })}
          />
        )}

        {/* Units */}
        {form.fieldType === 'units' && (
          <UnitsSection
            unitSource={form.unitSource} unitCategory={form.unitCategory} unitValuesArray={form.unitValuesArray}
            errors={{ unitCategory: errors.unitCategory, unitValuesArray: errors.unitValuesArray }}
            submitted={submitted}
            onSourceChange={(src) => patch({ unitSource: src, unitCategory: '', unitValuesArray: [] })}
            onCategoryChange={(cat) => patch({ unitCategory: cat })}
            onUnitAdd={addUnit} onUnitRemove={removeUnit} onUnitChange={changeUnit}
          />
        )}

        {/* File References */}
        {form.fieldType === 'file' && (
          <FileSection
            referenceFile={form.referenceFile}
            referenceSpreadsheetFile={form.referenceSpreadsheetFile}
            onUploadFile={handleUploadFile}
            onRemoveFile={handleRemoveFile}
            onOpenFile={handleOpenFile}
            onLinkSpreadsheet={handleLinkSpreadsheet}
            onRemoveSpreadsheet={handleRemoveSpreadsheet}
            isUploading={isUploading}
            uploadError={uploadError}
          />
        )}

        {/* Group — Child Fields */}
        {form.fieldType === 'group' && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Child Fields
            </Typography>

            {form.children.map((child, idx) => {
              const ce = errors.children?.[idx] ?? {};
              return (
                <Box key={child.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
                  {/* Child Name + Type */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                    <TextField
                      label="Child Field Name" size="small" sx={{ flex: 1 }}
                      placeholder="Enter child field name"
                      value={child.fieldName}
                      onChange={(e) => patchChild(idx, { fieldName: e.target.value })}
                      error={submitted && !!ce.fieldName}
                      helperText={submitted && ce.fieldName}
                    />
                    <TextField
                      select label="Child Field Type" size="small" sx={{ flex: 1 }}
                      value={child.fieldType}
                      onChange={(e) => handleChildFieldTypeChange(idx, e.target.value as CheckpointFieldType)}
                    >
                      {CHILD_FIELD_TYPES.map((ft) => (
                        <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  {child.fieldType === 'dropdown' && (
                    <>
                      <Divider sx={{ mb: 1.5 }} />
                      <DropdownSection
                        dropdown={child.dropdown} error={ce.dropdown} submitted={submitted}
                        onAdd={(lbl) => addChildDropdown(idx, lbl)}
                        onRemove={(id) => removeChildDropdown(idx, id)}
                        onChange={(id, lbl) => changeChildDropdown(idx, id, lbl)}
                      />
                    </>
                  )}

                  {child.fieldType === 'range' && (
                    <>
                      <Divider sx={{ mb: 1.5 }} />
                      <RangeSection
                        rangeMin={child.rangeMin} rangeMax={child.rangeMax}
                        errors={{ rangeMin: ce.rangeMin, rangeMax: ce.rangeMax }}
                        submitted={submitted}
                        onChange={(min, max) => patchChild(idx, { rangeMin: min, rangeMax: max })}
                      />
                    </>
                  )}

                  {child.fieldType === 'units' && (
                    <>
                      <Divider sx={{ mb: 1.5 }} />
                      <UnitsSection
                        unitSource={child.unitSource} unitCategory={child.unitCategory} unitValuesArray={child.unitValuesArray}
                        errors={{ unitCategory: ce.unitCategory, unitValuesArray: ce.unitValuesArray }}
                        submitted={submitted}
                        onSourceChange={(src) => patchChild(idx, { unitSource: src, unitCategory: '', unitValuesArray: [] })}
                        onCategoryChange={(cat) => patchChild(idx, { unitCategory: cat })}
                        onUnitAdd={(v) => addChildUnit(idx, v)}
                        onUnitRemove={(id) => removeChildUnit(idx, id)}
                        onUnitChange={(id, v) => changeChildUnit(idx, id, v)}
                      />
                    </>
                  )}

                  {child.fieldType === 'file' && (
                    <>
                      <Divider sx={{ mb: 1.5 }} />
                      {/* Hidden file input for this child's reference file upload */}
                      <input
                        ref={(el) => { childFileInputRefs.current[idx] = el; }}
                        type="file"
                        style={{ display: 'none' }}
                        onChange={(e) => handleChildFileInputChange(idx, e)}
                      />
                      <FileSection
                        referenceFile={child.referenceFile}
                        referenceSpreadsheetFile={child.referenceSpreadsheetFile}
                        onUploadFile={() => handleUploadFileChild(idx)}
                        onRemoveFile={() => handleRemoveFileChild(idx)}
                        onOpenFile={() => handleOpenFileChild(idx)}
                        onLinkSpreadsheet={() => handleLinkSpreadsheetChild(idx)}
                        onRemoveSpreadsheet={() => handleRemoveSpreadsheetChild(idx)}
                        isUploading={!!isUploadingChild[idx]}
                        uploadError={uploadErrorChild[idx]}
                      />
                    </>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                      onClick={() => removeChild(idx)}>
                      Remove
                    </Button>
                  </Box>
                </Box>
              );
            })}

            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addChild}>
              Add Child
            </Button>
          </Box>
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {isEditing ? 'Save Changes' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
